import { useEffect, useState, useRef } from 'react';
import useSound from 'use-sound';
// SignalR
import { HubConnectionBuilder } from '@microsoft/signalr';
// Services
import { useGetUserNotificationMutation } from 'src/app/services/account.service';
import { useResolveNotificationMutation, useCloseNotificationMutation } from 'src/app/services/notification.service';
import { usePageVisibility } from 'src/app/hooks/usePageVisibility';
// store
import { useAppSelector, useAppDispatch } from '../../store/stateTypeHook';
import { updatePendingNotification } from '../../store/stateSlice/alertSlice';
// Components
import NotificationList from 'src/app/components/app-notifications/NotificationList';
// Globals
import { APP_NOTIFICATION } from 'src/globals';
// Shared
import { Toast, IToast } from 'src/app/shared/Toast';
// Interface
import { Notification as NotificationObject } from 'src/app/models/AppNotification';

enum AddOnProductType {
    ABC = 1,
    CarouselCollection,
}

// export enum NotificationType {
//     Collection = 1,
//     Injection,
//     Delivery,
//     CustomerSmsReply = 6,
//     MissingBag = 7,
//     FlightCancelled = 11,
//     FlightDelayed = 12,
// }

export enum NotificationStatus {
    Active = 1,
    ManuallyResolved,
    SystemResolved,
    Missed,
}

let timer;
let defaultTitle = true;

function NotificationsOverlay() {
    const { loggedinUser: user } = useAppSelector((s) => s.user);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [callGetUserNotificatin, notificationRes] = useGetUserNotificationMutation();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [resolveNotification, resolveRes] = useResolveNotificationMutation();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [closeNotification, closeRes] = useCloseNotificationMutation();

    const [connection, setConnection] = useState(null);
    const [notifications, setNotifications] = useState<NotificationObject[]>([]);
    const [showToast, setShowToast] = useState<IToast>({});
    const latestNotification = useRef(null);

    const [newNotifications, setNewNotifications] = useState(0);
    const newNotificationCountRef = useRef(0);
    const isVisible = usePageVisibility();
    const dispatch = useAppDispatch();
    const [play, { sound }] = useSound('/beep.mp3', { volume: 0.5 });
    latestNotification.current = notifications;

    const errorHandler = (message) => {
        setShowToast({
            show: true,
            sticky: false,
            severity: 'error',
            message: message,
        });
    };

    useEffect(() => {
        callGetUserNotificatin({})
            .unwrap()
            .then((res) => {
                const notifications = res.notifications
                    .filter((i) => i.notificationStatus === 'Active')
                    .map((n, i) => ({
                        NotificationId: n.notificationId,
                        BookingReference: n.bookingReference,
                        BookingJourneyReference: n.bookingJourneyReference,
                        NotificationType: n.notificationType,
                        NotificationStatus: NotificationStatus[n.notificationStatus],
                        NotificationWindow: n.notificationWindow,
                        ProductType: AddOnProductType[n.productType],
                        CreatedDateText: n.createdDateText,
                        CreatedTimeText: n.createdTimeText,
                        ResolvedDateText: n.resolvedDateText,
                        ResolvedTimeText: n.resolvedTimeText,
                        ResolvedUsername: n.resolvedUsername,
                        NotificationText: n.notificationText,
                    }));

                setNotifications((o) => [...o, ...notifications]);
            })
            .catch((err) => {
                console.log(err);
            });

        const newConnection = new HubConnectionBuilder()
            .withUrl(APP_NOTIFICATION, {
                headers: {
                    'x-ms-signalr-userid': user.userId,
                },
            })
            .withAutomaticReconnect()
            .build();

        setConnection(newConnection);
        /**
         * @Mock notifications
         */
        // setInterval(() => {
        // 	newNotificationCountRef.current = ++newNotificationCountRef.current;
        //     setNewNotifications(newNotificationCountRef.current);
        // }, 1000);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        clearInterval(timer);
        if (!isVisible) {
            if (newNotificationCountRef.current > 0 && !sound.isPlaying) {
                beep();
            }
            timer = setInterval(() => {
                if (newNotificationCountRef.current > 0) {
                    document.title = 'New notification (' + newNotificationCountRef.current + ')';
                }
                if (defaultTitle) {
                    document.title = 'Welcome | Airportr';
                }
                defaultTitle = !defaultTitle;
            }, 700);
        } else {
            newNotificationCountRef.current = 0;
            setNewNotifications(0);
            document.title = 'Welcome | Airportr';
        }

        return () => {};
    }, [isVisible, newNotifications]);

    useEffect(() => {
        if (connection) {
            connection
                .start()
                .then((result) => {
                    console.log(connection, 'Connected!');
                    connection.on('NewNotification', (data: NotificationObject) => {
                        if (data.NotificationStatus === 1) {
                            const updatedNotifications = [...latestNotification.current];
                            updatedNotifications.push(data);

                            setNotifications(updatedNotifications);
                            newNotificationCountRef.current = ++newNotificationCountRef.current;
                            setNewNotifications(newNotificationCountRef.current);
                        } else {
                            const resolvedNotification = {
                                ...latestNotification.current.find((i) => i.NotificationId === data.NotificationId),
                            };
                            dispatch(
                                updatePendingNotification({
                                    resolvedNofication: resolvedNotification,
                                    penddingToBeResolved: true,
                                })
                            );

                            const updatedNotifications = [...latestNotification.current].filter((n, i) => {
                                return n.NotificationId !== data.NotificationId;
                            });

                            setNotifications(updatedNotifications);
                        }
                        // console.table(JSON.parse(JSON.stringify(data)));
                    });
                })
                .catch((e) => {
                    console.log('Connection failed: ', e);
                    // errorHandler('Serve connection failed');
                });
        }
    }, [connection]);

    const notificationResolve = async (notificationId) => {
        const request = {
            userId: user.userId,
            notificationId,
        };

        const resolvedNotification = { ...latestNotification.current.find((i) => i.NotificationId === notificationId) };
        if (connection._connectionStarted) {
            resolveNotification(request)
                .unwrap()
                .then((res) => {
                    dispatch(
                        updatePendingNotification({
                            resolvedNofication: resolvedNotification,
                            penddingToBeResolved: true,
                        })
                    );
                    setNotifications((o) => {
                        return o.filter((i) => i.NotificationId !== notificationId);
                    });
                })
                .catch((err) => {
                    console.log(err);
                    errorHandler('Unable to resolve, please try again');
                });
        } else {
            errorHandler('Connection not built yet');
        }
    };

    const notificationClose = async (notificationId) => {
        const request = {
            userId: user.userId,
            notificationId,
        };

        if (connection._connectionStarted) {
            closeNotification(request)
                .unwrap()
                .then((res) => {
                    setNotifications((o) => {
                        return o.filter((i) => i.NotificationId !== notificationId);
                    });
                })
                .catch((err) => {
                    console.log(err);
                    errorHandler('Unable to remove, please try again');
                });
        } else {
            errorHandler('Connection not built yet');
        }
    };

    function beep() {
        // return new Promise((resolve, reject) => {
        // 	try {
        // 		let soundSrc = '/beep.mp3';
        // 		const sound = new Audio(soundSrc);
        // 		sound.volume = volume / 100;

        // 		sound.onended = () => {
        // 			resolve('beeped');
        // 		};

        // 		sound.play();
        // 	} catch (e) {
        // 		reject(e);
        // 	}
        // });
        play({});
    }

    return (
        <>
            <NotificationList
                notifications={notifications}
                notificationResolve={notificationResolve}
                notificationClose={notificationClose}
            />
            {showToast.show && <Toast {...showToast} onClose={(show: boolean) => setShowToast({})} />}
        </>
    );
}

export default NotificationsOverlay;
